import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { FILTER_FIELD_SIZES, registerModuleFilters } from '@/utils/input/filters';

import {
  buildActorIdentityColumn,
  buildActorParentColumn,
  mapActorCapabilityDetailField,
  mapActorDisplayDetailFields,
  renderActorCapabilityBadge,
  renderActorEnabledBadge,
} from '../channel-actors/display';
import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const channelEntityStatus = {
  [CoreEnums.ChannelEntityStatus.Active]: { status: 'success' },
  [CoreEnums.ChannelEntityStatus.Suspended]: { status: 'warning' },
  [CoreEnums.ChannelEntityStatus.Terminated]: { status: 'error' },
  [CoreEnums.ChannelEntityStatus.PendingApproval]: { status: 'processing' },
  [CoreEnums.ChannelEntityStatus.Rejected]: { status: 'rejected' },
};

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelEntityStatus, 'Channel.Actors'),
  enumSource: CoreEnums.ChannelEntityStatus,
  options: createEnumOptions(CoreEnums.ChannelEntityStatus, 'channelEntityStatus', {
    casing: 'camel',
  }),
  badgeMap: channelEntityStatus,
  badgeType: 'group-item',
};

const CompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'company',
  label: 'global.text.company',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'id',
};

const OwnerCompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'ownerCompany',
  label: 'global.text.ownerCompany',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'ownerCompanyId',
};

const CanCreateCustomersFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'canCreateCustomers',
  label: 'global.text.canCreateCustomers',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  badgeType: 'group-item',
};

const BillingResponsibilityFilter = {
  ...BaseFilters.EnumFilter,
  name: 'billingResponsibility',
  label: 'global.text.billingResponsibility',
  enumNamespace: getEnumNamespace(CoreEnums.ResellerBillingResponsibility, 'Channel.Actors'),
  enumSource: CoreEnums.ResellerBillingResponsibility,
  options: createEnumOptions(
    CoreEnums.ResellerBillingResponsibility,
    'resellerBillingResponsibility',
    { casing: 'camel' },
  ),
};

const IsActiveFilter = {
  ...Filters.IsActiveFilter,
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  StatusFilter,
  CompanyFilter,
  OwnerCompanyFilter,
  CanCreateCustomersFilter,
  BillingResponsibilityFilter,
  IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for status / isActive / canCreateCustomers.
 * Other enum selects use `_form.js` locale options.
 */
export function mapChannelResellerDetailFields(rows, { item }) {
  const mapped = mapActorDisplayDetailFields(rows, item);
  if (!item || !mapped?.length) return mapped;

  return mapped.map((row) => {
    if (row.name === 'status') {
      const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelEntityStatus, item.status);
      const config = resolved !== undefined ? channelEntityStatus[resolved] : null;
      return {
        ...row,
        badgeStatus: config?.status || 'default',
        badgeType: 'group-item',
      };
    }

    if (row.name === 'canCreateCustomers') {
      return mapActorCapabilityDetailField(row, item, 'canCreateCustomers');
    }

    return row;
  });
}

export function buildChannelResellerColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      sorter: true,
      width: 120,
      render: (_, record) => renderActorEnabledBadge(record, formatMessage),
    },
    {
      key: 'company/commercialName',
      title: formatMessage({ id: 'global.text.company' }),
      dataIndex: ['company', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    buildActorIdentityColumn({ formatMessage, highlight }),
    {
      key: 'ownerCompany/commercialName',
      title: formatMessage({ id: 'global.text.ownerCompany' }),
      dataIndex: ['ownerCompany', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    buildActorParentColumn({ formatMessage, highlight }),
    {
      key: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      render: (value) => {
        const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelEntityStatus, value);
        const config = resolved !== undefined ? channelEntityStatus[resolved] : null;
        if (!config) {
          return <Badge status="default" type="group-item" text={DEFAULT_EMPTY_VALUE} />;
        }
        return (
          <Badge
            status={config.status}
            type="group-item"
            text={formatMessage({
              id: getEnumTranslationKey(
                CoreEnums.ChannelEntityStatus,
                resolved,
                'channelEntityStatus',
              ),
            })}
          />
        );
      },
    },
    {
      key: 'canCreateCustomers',
      title: formatMessage({ id: 'global.text.canCreateCustomers' }),
      dataIndex: 'canCreateCustomers',
      align: 'center',
      sorter: true,
      render: (_, record) => renderActorCapabilityBadge(record, 'canCreateCustomers', formatMessage),
    },
    {
      key: 'billingResponsibility',
      title: formatMessage({ id: 'global.text.billingResponsibility' }),
      dataIndex: 'billingResponsibility',
      align: 'center',
      sorter: true,
      render: (value) => {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.ResellerBillingResponsibility,
          value,
        );
        if (resolved === undefined) {
          return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
        }
        return (
          <Badge
            status="default"
            type="process"
            text={formatMessage({
              id: getEnumTranslationKey(
                CoreEnums.ResellerBillingResponsibility,
                resolved,
                'resellerBillingResponsibility',
              ),
            })}
          />
        );
      },
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (record) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(record.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
